import { Box, MenuItem, Pagination, TextField } from '@mui/material'
import {
	gridPageCountSelector,
	gridPageSelector,
	gridPageSizeSelector,
	gridRowCountSelector,
	useGridApiContext,
	useGridSelector,
} from '@mui/x-data-grid-pro'
import React from 'react'

type Props = {
	pageSizeOptions: number[]
}

/**
 * Replaces MUI DataGrid's default pagination footer with a numeric
 * `<Pagination>` (first / prev / 1 2 3 … / next / last) so users can jump to
 * the last page with one click. Keeps the rows-per-page selector and the
 * "start–end of total" counter on the same line.
 *
 * Reads + writes pagination state via the grid's `apiRef` so it stays in sync
 * with whatever controlled `paginationModel` the parent passes to DataGridPro.
 */
export const GridPagination: React.FC<Props> = ({ pageSizeOptions }) => {
	const apiRef = useGridApiContext()
	const page = useGridSelector(apiRef, gridPageSelector)
	const pageCount = useGridSelector(apiRef, gridPageCountSelector)
	const pageSize = useGridSelector(apiRef, gridPageSizeSelector)
	const rowCount = useGridSelector(apiRef, gridRowCountSelector)

	const start = rowCount === 0 ? 0 : page * pageSize + 1
	const end = Math.min((page + 1) * pageSize, rowCount)

	const setPage = (next: number): void => {
		apiRef.current.setPaginationModel({ page: next, pageSize })
	}
	const setPageSize = (next: number): void => {
		apiRef.current.setPaginationModel({ page: 0, pageSize: next })
	}

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1.5,
				justifyContent: 'flex-end',
				flexWrap: 'wrap',
				width: '100%',
				px: 1,
				py: 0.5,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<Box component="span" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
					Rows per page:
				</Box>
				<TextField
					select
					size="small"
					variant="standard"
					value={pageSize}
					onChange={(e) => setPageSize(Number(e.target.value))}
					sx={{
						minWidth: 48,
						'& .MuiInput-underline:before, & .MuiInput-underline:after': { border: 'none' },
						'& .MuiSelect-select': { fontSize: '0.8125rem', py: 0 },
					}}
				>
					{pageSizeOptions.map((s) => (
						<MenuItem key={s} value={s}>
							{s}
						</MenuItem>
					))}
				</TextField>
			</Box>
			<Box component="span" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
				{start}–{end} of {rowCount}
			</Box>
			<Pagination
				count={Math.max(1, pageCount)}
				page={page + 1}
				onChange={(_, p) => setPage(p - 1)}
				showFirstButton
				showLastButton
				color="primary"
				size="small"
				siblingCount={1}
				boundaryCount={1}
				sx={{
					width: 'fit-content',
					flexShrink: 0,
					'& .MuiPaginationItem-root': { minWidth: 28, height: 28 },
				}}
			/>
		</Box>
	)
}
